import { Paths } from "@app/routes";
import { CardContent, ScrollArea } from "@atlas/core";
import { FilterBuilder } from "@atlas/sdk";
import type { IHubCatalog, IHubPackage } from "@models";
import type { IHubExport } from "@query";
import { useSearchHubCatalogs, useSearchHubPackages } from "@query";
import { PackageCard } from "@shared";
import { useCallback, useMemo } from "react";
import { createSearchParams, useNavigate } from "react-router";
import { CatalogItemCard } from "../../catalog/pages/catalog/CatalogItemCard";

export function ExpandedExportedItems({
  exportItem,
}: {
  exportItem: IHubExport;
}) {
  const navigate = useNavigate();

  const handleMoreOptions = useCallback(
    (action: string, catalog?: IHubCatalog) => {
      if (action === "details" && catalog?.catalogId != null) {
        navigate({
          pathname: `/${Paths.CatalogManager}/${Paths.CatalogDetails}`,
          search: createSearchParams({
            catalogId: String(catalog.catalogId),
            version: String(catalog.version),
          }).toString(),
        });
      }
    },
    [navigate],
  );

  const handlePackageClick = useCallback(
    (pkg: IHubPackage) => {
      const pkgId = pkg.packageId;
      if (pkgId == null || pkgId <= 0) return;
      navigate(`/${Paths.PackageManager}?packageId=${pkgId}&fromTab=packages`);
    },
    [navigate],
  );

  const exportInfo = useMemo(() => {
    // IHubExport has featureConfig.Features which is the JSON token payload
    const tokenPayload = exportItem.featureConfig?.Features;
    if (!tokenPayload) return null;

    try {
      const parsed = JSON.parse(tokenPayload);
      if (parsed.packages && Array.isArray(parsed.packages)) {
        return { type: "packages", ids: parsed.packages };
      }
      if (parsed.catalogs && Array.isArray(parsed.catalogs)) {
        return { type: "catalogs", catalogs: parsed.catalogs };
      }
    } catch {
      return null;
    }
    return null;
  }, [exportItem]);

  const packageFilter = useMemo(() => {
    const builder = new FilterBuilder();
    if (exportInfo?.type === "packages" && exportInfo.ids.length > 0) {
      builder.in("packageId", exportInfo.ids);
    } else {
      builder.equals("packageId", -1);
    }
    return builder.build();
  }, [exportInfo]);

  const { data: packagesData, isLoading: isLoadingPackages } =
    useSearchHubPackages(
      { filter: packageFilter },
      { enabled: exportInfo?.type === "packages" },
    );

  const packages = useMemo(
    () =>
      packagesData?.pages.flatMap(
        (page: { items: IHubPackage[] }) => page.items,
      ) ?? [],
    [packagesData],
  );

  const catalogIds = useMemo(() => {
    if (exportInfo?.type === "catalogs") {
      return exportInfo.catalogs.map(
        (c: { catalogId: number; version: number }) => c.catalogId,
      );
    }
    return [];
  }, [exportInfo]);

  const catalogFilter = useMemo(() => {
    const builder = new FilterBuilder();
    if (catalogIds.length > 0) {
      builder.in("catalogId", catalogIds);
    } else {
      builder.equals("catalogId", -1);
    }
    return builder.build();
  }, [catalogIds]);

  const { data: catalogsData, isLoading: isLoadingCatalogs } =
    useSearchHubCatalogs(
      { filter: catalogFilter },
      { enabled: exportInfo?.type === "catalogs" },
    );

  const catalogs = useMemo(
    () =>
      catalogsData?.pages.flatMap(
        (page: { items: IHubCatalog[] }) => page.items,
      ) ?? [],
    [catalogsData],
  );

  if (!exportInfo) {
    return (
      <div className="p-4 text-muted-foreground">
        Unable to parse export data
      </div>
    );
  }

  if (exportInfo.type === "packages") {
    return (
      <ScrollArea className="bg-muted/30 max-h-145">
        <div className="p-4 grid auto-rows-auto grid-cols-[repeat(auto-fill,345px)] gap-4 justify-start">
          {isLoadingPackages
            ? Array.from({ length: Math.min(exportInfo.ids.length, 3) }).map(
                (_, i) => (
                  <CardContent
                    key={i}
                    isLoading={true}
                    className="h-[263px] w-[345px] rounded-xl"></CardContent>
                ),
              )
            : packages.map((pkg) => (
                <PackageCard
                  key={pkg.packageId}
                  pkg={pkg}
                  catalogCount={pkg.catalogRelations?.length ?? 0}
                  onClick={() => handlePackageClick(pkg)}
                />
              ))}
        </div>
      </ScrollArea>
    );
  }

  if (exportInfo.type === "catalogs") {
    return (
      <ScrollArea className="bg-muted/30 max-h-106">
        <div className="p-4 grid auto-rows-auto grid-cols-[repeat(auto-fill,345px)] gap-4 justify-start">
          {isLoadingCatalogs
            ? Array.from({
                length: Math.min(exportInfo.catalogs.length, 3),
              }).map((_, i) => (
                <CardContent
                  key={i}
                  isLoading={true}
                  className="h-[188px] w-[345px] rounded-xl"></CardContent>
              ))
            : catalogs.map((catalog) => (
                <CatalogItemCard
                  key={`${catalog.catalogId}-${catalog.version}`}
                  catalog={catalog}
                  showSubscribeAction={false}
                  onMoreOptions={handleMoreOptions}
                />
              ))}
        </div>
      </ScrollArea>
    );
  }

  return null;
}
